import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useContentOperations } from "@/hooks/useContentOperations";
import { usePersistentViewMode } from "@/hooks/usePersistentViewMode";
import { useFocusedContent } from "@/hooks/focused-content/useFocusedContent";
import { toast } from "sonner";
import ContentHeader from "@/components/focused-content/ContentHeader";
import ContentContainer from "@/components/focused-content/ContentContainer";
import ContentLoader from "@/components/focused-content/ContentLoader";
import ContentNotFound from "@/components/focused-content/ContentNotFound";
import RefreshButton from "@/components/dashboard/collection-grid/RefreshButton";
import { ContentItem } from "@/components/dashboard/content-card/types";
import { useMetadata } from "@/hooks/useMetadata";
import { APP_METADATA } from "@/constants/metadata";
import { useQueryClient } from "@tanstack/react-query";

const FocusedContent = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { handleDelete, handleArchive } = useContentOperations();
  const { viewMode, setViewMode } = usePersistentViewMode();
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [hasAttemptedLoad, setHasAttemptedLoad] = useState(false);
  const [webClipError, setWebClipError] = useState<{statusCode?: number; details?: string} | null>(null);
  
  const { 
    id, 
    contentItem, 
    isLoading, 
    refetch, 
    handleRelatedContentClick, 
    isReWebClipping,
    handleReWebClip,
    isExtensionAvailable 
  } = useFocusedContent();
  
  useMetadata({ 
    title: contentItem ? `${contentItem.title} - Clustr` : APP_METADATA.default.title,
    description: contentItem?.description || APP_METADATA.default.description,
    image: APP_METADATA.default.image
  });

  // Standardized hard refresh function that works reliably
  const performHardRefresh = useCallback(() => {
    console.log("[REFRESH] Executing force reload with cache bypass");
    toast.info("Hard refreshing page...");
    
    // Use setTimeout to ensure toast is visible
    setTimeout(() => {
      console.log("[REFRESH] Executing window.location.reload()");
      // Force a complete page reload, ignoring cache
      window.location.reload();
    }, 300);
    
    // Return a promise to maintain type compatibility
    return Promise.resolve();
  }, []);

  // Enhanced comprehensive refetch function with multiple refetches and explicit invalidation
  const handleComprehensiveRefetch = useCallback(async () => {
    console.log("[FocusedContent DEBUG] Performing comprehensive data refresh");
    toast.info("Refreshing content data...");
    
    try {
      // First, invalidate related queries to ensure fresh data
      if (id) {
        await queryClient.invalidateQueries({ queryKey: ["contentItem", id] });
        
        // Invalidate related items that might be connected
        await queryClient.invalidateQueries({ 
          predicate: (query) => {
            const queryKey = Array.isArray(query.queryKey) ? query.queryKey[0] : query.queryKey;
            return (
              queryKey === "relatedContent" || 
              queryKey === "content-tags" ||
              queryKey === "contentMetadata"
            );
          }
        });
      }
      
      // Then perform the actual refetch
      await refetch();
      
      // Add a second refetch after a small delay to ensure we have the latest data
      setTimeout(async () => {
        try {
          if (id) {
            await queryClient.refetchQueries({ queryKey: ["contentItem", id] });
            console.log("[FocusedContent DEBUG] Secondary refetch completed for extra freshness");
          }
        } catch (err) {
          console.error("[FocusedContent DEBUG] Error during secondary refetch:", err);
        }
      }, 1000);
      
      toast.success("Content refreshed successfully");
      console.log("[FocusedContent DEBUG] Data refreshed successfully");
    } catch (err) {
      console.error("[FocusedContent DEBUG] Error during comprehensive refresh:", err);
      toast.error("Failed to refresh content");
    }
  }, [id, queryClient, refetch]);

  // Keyboard shortcut effect for hard refresh - Updated to ensure reliability
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "e" && document.activeElement?.tagName !== "INPUT") {
        // Prevent default browser behavior
        e.preventDefault();
        e.stopPropagation();
        
        console.log("[HOTKEY] Cmd/Ctrl+E pressed - executing hard refresh");
        toast.info("Hard refreshing page...");
        
        // Use timeout to ensure toast is shown
        setTimeout(() => {
          console.log("[HOTKEY] Executing window.location.reload()");
          window.location.reload();
        }, 300);
      }
    };
    
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  useEffect(() => {
    if (contentItem) {
      const item = contentItem as ContentItem;
      console.log("[FocusedContent DEBUG] Content loaded:", {
        id: item?.id,
        title: item?.title,
        description: item?.description, 
        hasDescription: !!item?.description,
        collection_id: item?.collection_id,
        collection_name: (item as any)?.collection_name
      });
    }
  }, [contentItem]);

  useEffect(() => {
    if (!isLoading) {
      setHasAttemptedLoad(true);
    }
  }, [isLoading]);

  useEffect(() => {
    if (hasAttemptedLoad && !isLoading && !contentItem) {
      console.log("[FocusedContent DEBUG] Content not found after loading, navigating to dashboard");
      navigate('/dashboard');
    }
  }, [contentItem, isLoading, navigate, hasAttemptedLoad]);

  useEffect(() => {
    const initialRefresh = async () => {
      console.log("[FocusedContent DEBUG] Initial mount, forcing data refresh");
      try {
        await refetch();
      } catch (err) {
        console.error("[FocusedContent DEBUG] Error during initial refresh:", err);
      }
    };
    
    initialRefresh();
  }, [id, refetch]);
  
  // Enhanced re-web-clip handler with soft re-clipping by default
  const handleCustomReWebClip = async () => {
    setWebClipError(null);
    try {
      toast.info("Soft re-clipping content...");
      const result = await handleReWebClip(); // This now uses 'soft' level by default
      
      if (result.success) {
        toast.success("Content updated successfully with soft re-clipping!");
        console.log("[SOFT RE-CLIP] Success - forcing hard reload");
        
        // Ensure page refresh happens after success
        setTimeout(() => {
          console.log("[SOFT RE-CLIP] Executing window.location.reload()");
          window.location.reload();
        }, 300);
        
        return { success: true };
      } else if (!result.success) {
        toast.error("Failed to soft re-clip content");
        
        if (result.statusCode || result.errorDetails) {
          setWebClipError({
            statusCode: result.statusCode,
            details: result.errorDetails
          });
        }
        
        return result;
      }
      
      return { success: false, error: "Unknown error" };
    } catch (error) {
      console.error("Error during soft re-web-clip:", error);
      toast.error("Error during soft re-clipping");
      
      return { success: false, error };
    }
  };

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <div className="flex-none">
        {contentItem && (
          <ContentHeader 
            contentItem={contentItem}
            viewMode={viewMode}
            onViewModeChange={setViewMode}
            onEditClick={() => setEditDialogOpen(true)}
            onArchiveClick={() => {
              if (id) handleArchive(id);
            }}
            onDeleteClick={() => {
              if (id) {
                handleDelete(id);
                navigate('/dashboard');
              }
            }}
            refreshButton={
              <RefreshButton 
                onClick={handleCustomReWebClip}
                isRefreshing={isReWebClipping}
                tooltip={isExtensionAvailable ? "Soft Re-Clip Content (Browser Extension)" : "Soft Re-Clip Content"}
              />
            }
            refetchContent={performHardRefresh}
          />
        )}
      </div>

      {isExtensionAvailable && (
        <div className="bg-green-50 border-l-4 border-green-500 p-3 mb-3 mx-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-green-500" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-green-700">
                Clustr browser extension detected. Content extraction will use your browser context for better results.
                <span className="block mt-1 font-medium">
                  TIP: Use Ctrl/Cmd+R for Soft Re-Clip (preserves existing data) or Ctrl/Cmd+M for Mid Re-Clip (overwrites data but preserves manual thumbnails).
                </span>
              </p>
            </div>
          </div>
        </div>
      )}

      {webClipError && (
        <div className="bg-amber-50 border-l-4 border-amber-500 p-4 mb-4 mx-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-amber-500" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-amber-700">
                <strong>Web-clipping failed:</strong> {webClipError.statusCode === 403 
                  ? "The website is blocking our web clipper. Try setting a manual thumbnail instead."
                  : webClipError.statusCode === 404
                  ? "The page could not be found. Please check if the URL is still valid."
                  : webClipError.statusCode && webClipError.statusCode >= 500
                  ? "The website is unavailable or responding with errors."
                  : "Failed to extract metadata from this content."
                }
              </p>
            </div>
          </div>
        </div>
      )}

      {isLoading ? (
        <ContentLoader />
      ) : contentItem ? (
        <ContentContainer 
          contentItem={contentItem}
          contentId={id || ''}
          viewMode={viewMode}
          onRelatedContentClick={handleRelatedContentClick}
          refetch={performHardRefresh}
        />
      ) : (
        <ContentNotFound />
      )}
    </div>
  );
};

export default FocusedContent;
